import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const emptyForm = () => ({
  name: "", connectionId: "", type: "manual", mediaId: "", keyword: "",
  publicReply: "", privateMessage: "", link: ""
});

const FlowBuilderModal = ({ open, onClose, flowId, nameWebhook = "", onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const [connections, setConnections] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const set = (key, value) => setForm(previous => ({ ...previous, [key]: value }));

  useEffect(() => {
    if (!open) return;
    let active = true;
    setForm({ ...emptyForm(), name: nameWebhook || "" });
    api.get("/whatsapp").then(({ data }) => {
      if (active) setConnections(Array.isArray(data) ? data : data.whatsapps || []);
    }).catch(toastError);
    if (flowId) {
      api.get(`/flowbuilder/${flowId}`).then(({ data }) => {
        if (!active || !data.flow) return;
        const flow = data.flow;
        setForm({ ...emptyForm(), name: flow.name || "", connectionId: flow.connectionId || "",
          ...flow.triggerConfig, type: flow.triggerConfig?.type || "manual" });
      }).catch(toastError);
    }
    return () => { active = false; };
  }, [open, flowId, nameWebhook]);

  const connection = connections.find(item => Number(item.id) === Number(form.connectionId));
  useEffect(() => {
    if (!open || connection?.channel !== "instagram" || form.type !== "comment_keyword") return;
    let active = true;
    setLoadingPosts(true);
    api.get(`/flowbuilder/instagram-media/${connection.id}`).then(({ data }) => {
      if (active) setPosts(data.posts || []);
    }).catch(error => {
      if (active) { setPosts([]); toastError(error); }
    }).finally(() => { if (active) setLoadingPosts(false); });
    return () => { active = false; };
  }, [open, connection?.id, connection?.channel, form.type]);

  const save = async () => {
    if (form.name.trim().length < 2 || !form.connectionId) {
      toast.error("Informe o nome e selecione a conexão do fluxo.");
      return;
    }
    if (form.type === "comment_keyword" && (!form.mediaId || !form.keyword.trim() || !form.privateMessage.trim() || !form.link.trim())) {
      toast.error("Selecione o post e informe a palavra-chave, a mensagem e o link do Direct.");
      return;
    }
    const payload = {
      name: form.name.trim(), connectionId: Number(form.connectionId),
      triggerConfig: {
        type: connection?.channel === "instagram" ? form.type : "manual",
        ...(form.type === "comment_keyword" ? {
          mediaId: form.mediaId, keyword: form.keyword.trim(),
          privateMessage: form.privateMessage.trim(), publicReply: form.publicReply.trim(),
          link: form.link.trim()
        } : {})
      }
    };
    setLoading(true);
    try {
      let savedFlow;
      if (flowId) {
        const { data } = await api.put("/flowbuilder", { ...payload, flowId });
        savedFlow = data;
      } else {
        const { data } = await api.post("/flowbuilder", payload);
        savedFlow = data;
      }
      toast.success("Fluxo salvo com a conexão e o gatilho selecionados.");
      onSave(payload.name, savedFlow);
      onClose();
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle>{flowId ? "Configurar fluxo" : "Criar fluxo de conversa"}</DialogTitle>
      <DialogContent dividers>
        <TextField fullWidth margin="normal" variant="outlined" label="Nome do fluxo" value={form.name}
          onChange={event => set("name", event.target.value)} />
        <TextField select fullWidth margin="normal" variant="outlined" label="Conexão" value={form.connectionId}
          onChange={event => { setForm(previous => ({ ...previous, connectionId: event.target.value, type: "manual", mediaId: "" })); setPosts([]); }}>
          {connections.map(item => <MenuItem key={item.id} value={item.id}>
            {item.name} — {item.channel === "instagram" ? "Instagram" : item.waba_id || item.phone_number_id ? "WhatsApp Oficial" : item.channel === "facebook" ? "Facebook" : "WhatsApp"}
          </MenuItem>)}
        </TextField>
        {flowId && connection?.channel === "instagram" && <>
          <TextField select fullWidth margin="normal" variant="outlined" label="Quando iniciar" value={form.type}
            onChange={event => set("type", event.target.value)}>
            <MenuItem value="manual">Somente quando vinculado manualmente</MenuItem>
            <MenuItem value="direct_first_message">Primeira mensagem no Direct</MenuItem>
            <MenuItem value="story_reply">Resposta a um story</MenuItem>
            <MenuItem value="comment_keyword">Comentário com palavra-chave em um post</MenuItem>
            <MenuItem value="new_follower" disabled>Novo seguidor — indisponível na API da Meta</MenuItem>
          </TextField>
          {form.type === "story_reply" && <Typography variant="body2" color="textSecondary">
            O fluxo começa quando alguém responde ao story pelo Direct. Monte a resposta nos blocos do editor.
          </Typography>}
          {form.type === "comment_keyword" && <>
            <TextField select fullWidth margin="normal" variant="outlined" label="Post ou reel" value={form.mediaId}
              disabled={loadingPosts} onChange={event => set("mediaId", event.target.value)}
              helperText={loadingPosts ? "Carregando posts..." : "A regra só será aplicada à publicação escolhida."}>
              {posts.map(post => <MenuItem key={post.id} value={post.id}>
                {post.media_type === "VIDEO" ? "Reel" : "Post"} — {(post.caption || post.permalink || post.id).slice(0, 75)}
              </MenuItem>)}
            </TextField>
            <TextField fullWidth margin="normal" variant="outlined" label="Palavra-chave do comentário" value={form.keyword}
              onChange={event => set("keyword", event.target.value)} helperText="A palavra pode estar em qualquer parte do comentário." />
            <TextField fullWidth margin="normal" variant="outlined" label="Resposta pública (opcional)" value={form.publicReply}
              onChange={event => set("publicReply", event.target.value)} helperText="Exemplo: Enviei o link no seu Direct!" />
            <TextField fullWidth multiline rows={3} margin="normal" variant="outlined" label="Mensagem privada no Direct" value={form.privateMessage}
              onChange={event => set("privateMessage", event.target.value)} />
            <TextField fullWidth margin="normal" variant="outlined" label="Link HTTPS a enviar" value={form.link}
              onChange={event => set("link", event.target.value)} helperText="O Instagram permite uma resposta privada inicial por comentário. Se a pessoa responder, a conversa aparece em Atendimentos." />
          </>}
        </>}
        {connection && connection.channel !== "instagram" && <Typography variant="body2" color="textSecondary">
          Fluxo vinculado a esta conexão. Configure o disparo nas regras e opções do canal WhatsApp.
        </Typography>}
        {!flowId && connection?.channel === "instagram" && <Typography variant="body2" color="textSecondary">
          O fluxo será criado vazio. No editor, use os blocos da categoria Instagram para escolher e configurar o gatilho.
        </Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button onClick={save} color="primary" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderModal;
